<template>
	<nta-dialog
		:show="showModal"
		title="Add Dataobject"
		height="auto"
		width="50em"
		:symbol-lang="domainStore.languagePreference"
		@close="emits('close')"
	>
		<template #default>
			<div class="d-flex ma-2">
				<div class="left-side d-flex align-center justify-center">
					<v-btn variant="tonal" color="primary" class="mt-2" block @click="createNewInstance">
						<v-icon>mdi-plus</v-icon> Create New Instance
					</v-btn>
				</div>
				<v-divider vertical class="mx-2"></v-divider>
				<div class="right-side">
					<!-- Parent -->
					<div class="ma-1">Parent: {{ parentTerm() }}</div>
					<!-- Concept Dataobject Selection -->
					<v-autocomplete
						v-model="DOConcept"
						:items="allowedConceptDataobjects"
						:item-title="
							(acd) =>
								symbolsStore.term({
									id: acd.refersTo,
									terminology: acd.terminology,
									lang: domainStore.languagePreference,
								})
						"
						:item-value="(acd) => acd.id"
						return-object
						label="Concept Dataobject"
						hide-details
						class="ma-1"
						variant="solo"
					></v-autocomplete>

					<!-- Concept Attribute Selection -->
					<v-autocomplete
						v-model="ATTRConcept"
						:items="allowedConceptAttributes"
						:item-title="
							(a) =>
								symbolsStore.term({
									id: a.refersTo,
									terminology: a.terminology,
									lang: domainStore.languagePreference,
								})
						"
						:item-value="(a) => a.id"
						return-object
						label="Concept Attribute"
						:disabled="!DOConcept"
						hide-details
						class="ma-1"
						variant="solo"
					></v-autocomplete>

					<!-- Attribute Value -->
					<v-autocomplete
						v-if="enumItems.length > 0"
						v-model="ATTRValue"
						:items="enumItems"
						:item-title="
							(ei) =>
								symbolsStore.term({
									id: ei.refersTo,
									terminology: ei.terminology,
									lang: domainStore.languagePreference,
								})
						"
						:item-value="(ei) => ei.id"
						label="Attribute Value"
						:disabled="!ATTRConcept"
						:return-object="false"
						hide-details
						class="ma-1"
						variant="solo"
					>
						<template #selection="{ item, index }">
							<!-- Custom rendering for selected EnumItems -->
							<span>{{
								symbolsStore.term({
									id: item.raw.refersTo,
									terminology: item.raw.terminology,
									lang: domainStore.languagePreference,
								})
							}}</span>
						</template>
					</v-autocomplete>
					<v-text-field
						v-else
						v-model="ATTRValue"
						label="Attribute Value"
						:disabled="!ATTRConcept"
						:type="datatype?.id === 'number' ? 'number' : 'text'"
						hide-details
						class="ma-1"
						variant="solo"
					>
					</v-text-field>

					<v-btn variant="tonal" color="primary" class="mt-2" block :disabled="!DOConcept" @click="search">
						<v-icon>mdi-magnify</v-icon> Search Existing Instances
					</v-btn>
				</div>
			</div>

			<v-divider class="my-2"></v-divider>

			<!-- Display Search Results-->
			<div class="d-flex justify-start align-center flex-wrap ma-2" style="max-height: 400px; overflow-y: auto">
				<SearchResult
					v-for="result in searchResults"
					:key="result.instanceDataobject.id"
					:instance-dataobject="result.instanceDataobject"
					:displayed-attributes="result.displayedAttributes"
					:parent-instance-dataobject-id="compProps.parentInstanceDataobjectId"
					:source-instance-workspace-id="result.sourceInstanceWorkspaceId"
					@imported="emits('close')"
				/>
			</div>
		</template>
	</nta-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDomainStore } from "../../../../store/domain";
import { useInstanceWorkspacesStore } from "../../../../store/i-workspace";
import { useGraphStore } from "../../../../store/graph";
import { useSymbolsStore } from "../../../../store/symbols";
import { useInstanceDataspacesStore } from "../../../../store/i-dataspace";
import {
	ConceptDataobject,
	ConceptAttribute,
	SearchInstanceData,
	TemplateDataobject,
} from "@lr-notitia/notitia-operator/schemas";
import { api } from "@lr-notitia/notitia-operator/client";
import SearchResult from "./SearchResult.vue";

const domainStore = useDomainStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();
const instanceDataspacesStore = useInstanceDataspacesStore();

const emits = defineEmits(["close"]);
const compProps = defineProps({
	showModal: {
		type: Boolean,
		required: true,
	},
	templateId: {
		type: String,
		required: true,
	},
	parentInstanceDataobjectId: {
		type: String,
		required: false,
		default: null,
	},
});

const instanceWorkspace = computed(() => instanceWorkspacesStore.openedWorkspace);
const allowedTemplateDataobjects = computed(() => {
	if (!compProps.parentInstanceDataobjectId) return [];

	let parentInstanceDataobject = graphStore.instance.dataobject.byId(compProps.parentInstanceDataobjectId);
	if (!parentInstanceDataobject) return [];
	let parentTemplateDataobject = graphStore.template.dataobject.byId(parentInstanceDataobject.template);
	if (!parentTemplateDataobject) return [];

	let td = parentTemplateDataobject.hasA
		.map((id) => graphStore.template.dataobject.byId(id))
		.filter((d) => !!d)
		.filter((d) => {
			let maxCardinality = d.cardinality?.max || -1;
			let countExisting =
				parentInstanceDataobject.hasA
					.map((hasA) => {
						let workspaceDataobject = graphStore.instance.dataobject.byId(hasA);
						if (!workspaceDataobject) return null;
						return workspaceDataobject.template === d.id;
					})
					.filter((x) => x).length || 0;
			if (maxCardinality === -1) return true;
			if (countExisting < maxCardinality) return true;
			return false;
		});
	return td;
});
const allowedConceptDataobjects = computed(() => {
	return allowedTemplateDataobjects.value
		.map((td) => {
			let conceptDataobjectId = td.concept;
			return graphStore.concept.dataobject.byId(conceptDataobjectId);
		})
		.filter((d) => !!d);
});

const automaticDOConcept = computed(() => {
	let template = graphStore.template.dataobject.byId(compProps.templateId);
	if (!template) return null;
	return graphStore.concept.dataobject.byId(template.concept) || null;
});

const DOConcept = ref<ConceptDataobject | null>(null);

function getNestedConceptAttributes(conceptDataobject: ConceptDataobject, visitedIds = new Set<string>()) {
	if (visitedIds.has(conceptDataobject.id)) return [];
	visitedIds.add(conceptDataobject.id);

	// Own attributes
	let conceptAttributes = conceptDataobject.consistsOf
		.map((id) => graphStore.concept.attribute.byId(id))
		.filter((a) => !!a);

	// Child attributes
	let childrenConceptDataobjects = [];
	for (let childId of conceptDataobject.hasA) {
		let childConceptDataobject = graphStore.concept.dataobject.byId(childId);
		if (childConceptDataobject) {
			childrenConceptDataobjects.push(childConceptDataobject);
		}
	}
	for (let childId of conceptDataobject.composedBy) {
		let childConceptDataobject = graphStore.concept.dataobject.byId(childId);
		if (childConceptDataobject) {
			childrenConceptDataobjects.push(childConceptDataobject);
		}
	}
	for (let childConceptDataobject of childrenConceptDataobjects) {
		let childConceptAttributes = getNestedConceptAttributes(childConceptDataobject, visitedIds);
		conceptAttributes.push(...childConceptAttributes);
	}

	return conceptAttributes;
}
const allowedConceptAttributes = computed(() => {
	if (!DOConcept.value) return [];
	return getNestedConceptAttributes(DOConcept.value);
});

const ATTRConcept = ref<ConceptAttribute | null>(null);

const datatype = computed(() => {
	if (!ATTRConcept.value) return null;
	return graphStore.descriptor.datatype.byId(ATTRConcept.value.datatype);
});

const enumItems = computed(() => {
	if (!datatype.value) return [];
	return datatype.value.enumerationItems.map((id) => graphStore.descriptor.enumItem.byId(id)).filter((ei) => !!ei);
});

const ATTRValue = ref<string | number | null>(null);

async function search() {
	if (!DOConcept.value) return;
	if (datatype.value?.id === "number") ATTRValue.value = Number(ATTRValue.value);
	if (!instanceWorkspace.value || !instanceDataspacesStore.openedDataspaceId) return;
	searchResults.value = await api()
		.domain(domainStore.domain.id)
		.workspace(instanceWorkspace.value.id)
		.dataobjects()
		.search({
			dataspaceId: instanceDataspacesStore.openedDataspaceId,
			conceptDataobjectId: DOConcept.value.id,
			conceptAttributeId: ATTRConcept.value?.id || undefined,
			attributeValue: ATTRValue.value || undefined,
		});
}

const searchResults = ref<SearchInstanceData[]>([]);

function parentTerm() {
	if (!compProps.parentInstanceDataobjectId) return "Root";
	let parentInstanceDataobject = graphStore.instance.dataobject.byId(compProps.parentInstanceDataobjectId);
	if (!parentInstanceDataobject) return "Root";
	let parentTemplateDataobject = graphStore.template.dataobject.byId(parentInstanceDataobject.template);
	if (!parentTemplateDataobject) return "Root";
	return symbolsStore.term({
		id: parentTemplateDataobject.concept,
		terminology: parentTemplateDataobject.terminology,
		lang: domainStore.languagePreference,
	});
}

async function createNewInstance() {
	let template = graphStore.graph.nodes[compProps.templateId] as TemplateDataobject;
	if (!template) return;
	await graphStore.instance.dataobject.add(template.concept, template.id, compProps.parentInstanceDataobjectId);
	emits("close");
}

watch(
	() => compProps.showModal,
	() => {
		if (!compProps.showModal) {
			DOConcept.value = null;
			ATTRConcept.value = null;
			ATTRValue.value = null;
			searchResults.value = [];
		}
	}
);
watch(DOConcept, () => {
	ATTRConcept.value = null;
	ATTRValue.value = null;
});
watch(ATTRConcept, () => {
	ATTRValue.value = null;
});
watch(automaticDOConcept, () => {
	if (!DOConcept.value) DOConcept.value = automaticDOConcept.value;
});
</script>

<style scoped lang="scss">
.left-side {
	width: 33%;
}

.right-side {
	width: 67%;
}
</style>
